import React, { useCallback, useEffect, useRef, useState } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import api from "../services/api";
import { useHelp } from "../contexts/HelpContext";
import { useAuth } from "../contexts/AuthContext";
import "./helpDrawer.css";

const MIN_WIDTH = 320;
const MAX_WIDTH = 900;
const DEFAULT_WIDTH = 520;
const WIDTH_KEY = "helpDrawerWidth";

const HelpDrawer = () => {
  const { helpSlug, isOpen, closeHelp } = useHelp();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftContentHtml, setDraftContentHtml] = useState("");
  const [width, setWidth] = useState(() => {
    const stored = parseInt(localStorage.getItem(WIDTH_KEY), 10);
    if (Number.isNaN(stored)) return DEFAULT_WIDTH;
    return Math.min(Math.max(stored, MIN_WIDTH), MAX_WIDTH);
  });
  const resizingRef = useRef(false);
  const editorRef = useRef(null);
  const quillRef = useRef(null);

  const fetchDocument = useCallback(async () => {
    if (!helpSlug || !isOpen) return;
    setLoading(true);
    setError("");
    try {
      const response = await api.get(`/help/document/${helpSlug}`);
      setDocumentData(response.data.document);
    } catch (err) {
      try {
        const params = new URLSearchParams({
          route_path: window.location.pathname,
        });
        if (isAdmin) {
          params.set("includeDrafts", "true");
        }
        const fallback = await api.get(`/help/documents?${params.toString()}`);
        const fallbackDoc = fallback.data?.documents?.[0] || null;

        if (fallbackDoc) {
          setDocumentData(fallbackDoc);
          setError("");
          return;
        }
      } catch (fallbackErr) {
        // Keep original behavior below when fallback lookup fails.
      }

      setDocumentData(null);
      setError(err.response?.data?.message || "Help document not found.");
    } finally {
      setLoading(false);
    }
  }, [helpSlug, isOpen, isAdmin]);

  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

  useEffect(() => {
    // Always reopen in view mode; prevents stale edit state across drawer sessions.
    if (isOpen) {
      setIsEditing(false);
      setSaving(false);
      setDraftTitle("");
      setDraftContentHtml("");
      setError("");
    } else {
      setIsEditing(false);
      setSaving(false);
    }
  }, [isOpen, helpSlug]);

  useEffect(() => {
    if (!documentData || isEditing) return;
    setDraftTitle(documentData.title || "");
    setDraftContentHtml(documentData.contentHtml || "");
  }, [documentData, isEditing]);

  useEffect(() => {
    if (!isEditing || !editorRef.current) return;

    if (!quillRef.current) {
      if (draftContentHtml) {
        editorRef.current.innerHTML = draftContentHtml;
      }

      const editor = new Quill(editorRef.current, {
        theme: "snow",
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
            ["clean"],
          ],
        },
      });

      editor.on("text-change", () => {
        setDraftContentHtml(editor.root.innerHTML);
      });

      quillRef.current = editor;
    } else if (quillRef.current.root.innerHTML !== draftContentHtml) {
      quillRef.current.root.innerHTML = draftContentHtml || "";
    }
  }, [isEditing, draftContentHtml]);

  useEffect(() => {
    if (!isEditing) {
      quillRef.current = null;
    }
  }, [isEditing]);

  useEffect(() => {
    const handleMove = (e) => {
      if (!resizingRef.current) return;
      const nextWidth = Math.min(
        Math.max(window.innerWidth - e.clientX, MIN_WIDTH),
        Math.min(MAX_WIDTH, window.innerWidth - 40),
      );
      setWidth(nextWidth);
    };
    const handleUp = () => {
      if (resizingRef.current) {
        resizingRef.current = false;
        localStorage.setItem(WIDTH_KEY, String(width));
      }
    };
    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
    };
  }, [width]);

  if (!isOpen) return null;

  const startInlineEdit = () => {
    setDraftTitle(documentData?.title || "");
    setDraftContentHtml(documentData?.contentHtml || "");
    setIsEditing(true);
  };

  const cancelInlineEdit = () => {
    setDraftTitle(documentData?.title || "");
    setDraftContentHtml(documentData?.contentHtml || "");
    setIsEditing(false);
  };

  const saveInlineEdit = async () => {
    if (!documentData?.id) return;

    try {
      setSaving(true);
      setError("");
      await api.put(`/help/document/${documentData.id}`, {
        title: draftTitle,
        contentHtml: draftContentHtml,
      });

      setDocumentData((prev) =>
        prev
          ? {
              ...prev,
              title: draftTitle,
              contentHtml: draftContentHtml,
            }
          : prev,
      );
      setIsEditing(false);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save help document.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className="help-drawer-overlay"
        onClick={closeHelp}
        aria-hidden="true"
      />
      <div
        className="help-drawer-panel"
        style={{ width }}
        role="dialog"
        aria-label="Help"
      >
        <div
          className="help-drawer-handle"
          onMouseDown={() => {
            resizingRef.current = true;
          }}
          aria-hidden="true"
        />
        <div className="border-b border-slate-200 px-5 py-4 flex items-center justify-between bg-slate-900 text-white">
          <div>
            <p className="text-sm uppercase tracking-wide text-slate-300">
              Help
            </p>
            <h2 className="text-lg font-semibold">
              {documentData?.title || "Help Center"}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && documentData?.slug && (
              <button
                type="button"
                onClick={startInlineEdit}
                className="px-3 py-1.5 text-sm font-medium rounded border border-slate-500 text-slate-100 hover:bg-slate-800 transition"
              >
                {isEditing ? "Editing" : "Edit"}
              </button>
            )}
            <button
              type="button"
              onClick={closeHelp}
              className="text-slate-300 hover:text-white transition text-2xl leading-none"
              aria-label="Close help"
            >
              ×
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {loading && (
            <div className="text-sm text-slate-500">
              Loading help content...
            </div>
          )}
          {!loading && error && (
            <div className="text-sm text-rose-600">
              {error}
              <div className="mt-3">
                <a className="text-primary-600 hover:underline" href="/help">
                  Go to Help Center
                </a>
              </div>
            </div>
          )}
          {!loading && !error && documentData && (
            <>
              {isEditing && isAdmin ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      value={draftTitle}
                      onChange={(e) => setDraftTitle(e.target.value)}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">
                      Content
                    </label>
                    <div
                      ref={editorRef}
                      className="bg-white"
                      style={{ minHeight: "260px" }}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={saveInlineEdit}
                      disabled={saving}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={cancelInlineEdit}
                      disabled={saving}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="help-content"
                  dangerouslySetInnerHTML={{ __html: documentData.contentHtml }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default HelpDrawer;
